import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import MonitorStats from "@/pages/admin/monitor/components/MonitorStats";
import MonitorCharts from "@/pages/admin/monitor/components/MonitorCharts";
import AlertPanel from "@/pages/admin/monitor/components/AlertPanel";
import { useMonitorData } from "@/pages/admin/monitor/hooks/useMonitorData";
import { useMonitorAlerts } from "@/pages/admin/monitor/hooks/useMonitorAlerts";
import { formatNumber } from "@/lib/format";

interface Notice {
  text: string;
  tone: "success" | "error";
}

export default function MonitorPage() {
  const { metrics, stats, loading, error, reload } = useMonitorData();
  const alerts = useMonitorAlerts();
  const [notice, setNotice] = useState<Notice | null>(null);
  const checkedRef = useRef(false);

  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  }, []);

  // 进入看板时静默跑一次阈值检测，及时把新触发的告警写入日志与通知。
  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    void alerts.runCheck().catch(() => undefined);
  }, [alerts]);

  const licenseBreakdown = [
    { label: "有效许可证", value: stats?.active_licenses ?? 0, tone: "bg-primary-500" },
    { label: "已过期", value: stats?.expired_licenses ?? 0, tone: "bg-secondary-500" },
    { label: "已暂停", value: stats?.suspended_licenses ?? 0, tone: "bg-accent-500" },
    { label: "已吊销", value: stats?.revoked_licenses ?? 0, tone: "bg-foreground-400" },
  ];
  const breakdownTotal = licenseBreakdown.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                管理后台
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">授权状态监控</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-line-chart-line" />
                  </span>
                  运营监控看板
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  授权状态监控
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  集中查看授权激活量、接口请求量与平台可用率的逐日趋势，并掌握当前许可证整体构成。可为可用率或请求量设置阈值，跌破时自动写入告警日志并推送站内通知，运营异常第一时间可见。
                </p>
              </div>
              <Link
                to="/admin"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                  <i className="ri-arrow-left-line" />
                </span>
                返回管理后台
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重新加载
              </button>
            </div>
          ) : (
            <>
              <MonitorStats metrics={metrics} loading={loading} />

              <div className="mt-6">
                <AlertPanel
                  thresholds={alerts.thresholds}
                  logs={alerts.logs}
                  loading={alerts.loading}
                  checking={alerts.checking}
                  onSave={alerts.saveThresholds}
                  onCheck={alerts.runCheck}
                  notify={notify}
                />
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">当前许可证构成</h3>
                    <p className="mt-1 text-xs text-foreground-500">
                      平台在册许可证共 {loading ? "--" : formatNumber(breakdownTotal)} 张，在线席位{" "}
                      {loading ? "--" : formatNumber(stats?.online_seats ?? 0)}
                    </p>
                  </div>
                </div>
                <div className="mt-5 flex h-3 w-full overflow-hidden rounded-full bg-background-200">
                  {licenseBreakdown.map((item) => {
                    const ratio = breakdownTotal > 0 ? (item.value / breakdownTotal) * 100 : 0;
                    if (ratio <= 0) return null;
                    return (
                      <span
                        key={item.label}
                        className={item.tone}
                        style={{ width: `${ratio}%` }}
                        aria-label={`${item.label} ${item.value}`}
                      />
                    );
                  })}
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {licenseBreakdown.map((item) => (
                    <div key={item.label} className="flex items-center gap-2.5">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${item.tone}`} />
                      <span className="text-sm text-foreground-700">{item.label}</span>
                      <span className="ml-auto font-medium text-foreground-950">
                        {loading ? "--" : formatNumber(item.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <MonitorCharts metrics={metrics} loading={loading} />
              </div>
            </>
          )}
        </section>
      </main>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm shadow-sm ${
              notice.tone === "success" ? "bg-primary-600 text-background-50" : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}